import React, { useMemo, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, MetricCard, PageHeader, TableToolbar } from './AdminUI';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';

const ContactsManagement = () => {
    const { apiCall } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedContact, setSelectedContact] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [tableSearch, setTableSearch] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            const response = await apiCall('/contact');
            const data = await response.json();
            setContacts(data.contacts || []);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const contact = contacts.find(c => c.id === id);
            const response = await apiCall(`/contact/${id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...contact, status })
            });

            if (response.ok) {
                await fetchContacts();
                alertSuccess('Updated', 'Contact status updated.');
            } else {
                alertError('Error', 'Error updating contact status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alertError('Error', 'Error updating contact status');
        }
    };

    const handleDelete = async (id) => {
        const { isConfirmed } = await confirmDelete({ title: 'Delete contact?', text: 'Are you sure? This cannot be undone.' });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/contact/${id}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchContacts();
                alertSuccess('Deleted', 'Contact deleted.');
            } else {
                alertError('Error', 'Error deleting contact');
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            alertError('Error', 'Error deleting contact');
        }
    };

    const handleViewDetails = (contact) => {
        setSelectedContact(contact);
        setShowDetailModal(true);
    };

    const statusOptions = ['new', 'contacted', 'qualified', 'converted', 'closed'];
    const statusColors = {
        'new': { bg: '#fef3c7', color: '#92400e', label: 'New' },
        'contacted': { bg: 'rgba(98, 182, 232, 0.15)', color: '#2F7FAE', label: 'Contacted' },
        'qualified': { bg: '#d1fae5', color: '#065f46', label: 'Qualified' },
        'converted': { bg: '#e0e7ff', color: '#3730a3', label: 'Converted' },
        'closed': { bg: '#f3f4f6', color: '#374151', label: 'Closed' }
    };

    const filteredContacts = useMemo(() => {
        const filteredByStatus = filter === 'all'
            ? contacts
            : contacts.filter(c => c.status === filter);
        const query = tableSearch.trim().toLowerCase();

        if (!query) return filteredByStatus;

        return filteredByStatus.filter((contact) => [
            contact.name,
            contact.email,
            contact.subject,
            contact.requestedService,
            contact.service,
            contact.status,
            contact.message
        ].some((value) => String(value || '').toLowerCase().includes(query)));
    }, [contacts, filter, tableSearch]);

    const stats = {
        total: contacts.length,
        new: contacts.filter(c => c.status === 'new').length,
        contacted: contacts.filter(c => c.status === 'contacted').length,
        qualified: contacts.filter(c => c.status === 'qualified').length,
        converted: contacts.filter(c => c.status === 'converted').length
    };

    const contactColumns = [
        { name: 'Name', selector: (row) => row.name, sortable: true },
        { name: 'Email', selector: (row) => row.email, sortable: true },
        { name: 'Subject', selector: (row) => row.subject, sortable: true },
        { name: 'Service', selector: (row) => row.requestedService || row.service || '-', sortable: true },
        {
            name: 'Status',
            cell: (row) => (
                <select
                    value={row.status}
                    onChange={(e) => handleUpdateStatus(row.id, e.target.value)}
                    style={{
                        padding: '4px 8px',
                        borderRadius: '0',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: 'none',
                        background: statusColors[row.status]?.bg || '#f3f4f6',
                        color: statusColors[row.status]?.color || '#374151',
                        cursor: 'pointer'
                    }}
                >
                    {statusOptions.map(status => (
                        <option key={status} value={status}>{statusColors[status].label}</option>
                    ))}
                </select>
            ),
            width: '140px',
            ignoreRowClick: true
        },
        {
            name: 'Date',
            selector: (row) => row.createdAt,
            sortable: true,
            cell: (row) => new Date(row.createdAt).toLocaleDateString()
        },
        {
            name: 'Actions',
            cell: (row) => (
                <span style={{ display: 'flex', gap: '8px' }}>
                    <button
                        type="button"
                        onClick={() => handleViewDetails(row)}
                        style={{
                            background: '#62B6E8',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                        title="View details"
                    >
                        <i className="fa fa-eye"></i>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleDelete(row.id)}
                        style={{
                            background: '#e53e3e',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </span>
            ),
            width: '120px',
            ignoreRowClick: true
        }
    ];

    const tableCustomStyles = {
        headCells: {
            style: {
                padding: '12px 16px',
                fontWeight: '600',
                fontSize: '14px',
                color: '#4a5568',
                backgroundColor: '#f7fafc',
                borderBottom: '2px solid #e2e8f0'
            }
        },
        cells: {
            style: {
                padding: '12px 16px',
                fontSize: '14px',
                color: '#4a5568'
            }
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading contacts..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Contact Form Submissions"
                    subtitle="Manage and track inquiries"
                />

                {/* Stats Cards */}
                <div className="analytics-metrics-grid">
                    <MetricCard label="Total" value={stats.total} icon="fa fa-list" accent="#62B6E8" detail="All submissions" />
                    <MetricCard label="New" value={stats.new} icon="fa fa-plus-circle" accent="#f59e0b" detail="Awaiting review" />
                    <MetricCard label="Contacted" value={stats.contacted} icon="fa fa-phone" accent="#3b82f6" detail="Followed up" />
                    <MetricCard label="Qualified" value={stats.qualified} icon="fa fa-check-circle" accent="#10b981" detail="Qualified leads" />
                    <MetricCard label="Converted" value={stats.converted} icon="fa fa-check-square-o" accent="#8b5cf6" detail="Won inquiries" />
                </div>

                {/* Filter */}
                <div className="admin-filter-bar">
                    <button
                        onClick={() => setFilter('all')}
                        className={`admin-filter-button ${filter === 'all' ? 'active' : ''}`}
                    >
                        All ({stats.total})
                    </button>
                    {statusOptions.map(status => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`admin-filter-button ${filter === status ? 'active' : ''}`}
                            style={filter === status ? {
                                background: statusColors[status].bg,
                                borderColor: statusColors[status].bg,
                                color: statusColors[status].color
                            } : null}
                        >
                            {statusColors[status].label} ({contacts.filter(c => c.status === status).length})
                        </button>
                    ))}
                </div>

                {/* Contacts Table */}
                <div className="card admin-data-card">
                    <TableToolbar
                        search={tableSearch}
                        onSearchChange={setTableSearch}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={setRowsPerPage}
                        placeholder="Search contacts..."
                    />
                    <DataTable
                        columns={contactColumns}
                        data={filteredContacts}
                        customStyles={tableCustomStyles}
                        pagination
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[10, 20, 30]}
                        paginationComponentOptions={{ noRowsPerPage: true }}
                        progressPending={loading}
                        progressComponent={<div style={{ padding: '24px', color: '#718096' }}>Loading...</div>}
                        noDataComponent={
                            <EmptyState
                                icon="fa fa-envelope-o"
                                title="No contact submissions found"
                            />
                        }
                        striped
                        dense
                    />
                </div>

                {/* Detail Modal */}
                {showDetailModal && selectedContact && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '0',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    Contact Details
                                </h2>
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Name</label>
                                    <div style={{ fontSize: '16px', color: '#1a202c', fontWeight: '500' }}>{selectedContact.name}</div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Email</label>
                                    <div style={{ fontSize: '16px', color: '#1a202c' }}>
                                        <a href={`mailto:${selectedContact.email}`} style={{ color: '#62B6E8', textDecoration: 'none' }}>
                                            {selectedContact.email}
                                        </a>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Phone</label>
                                    <div style={{ fontSize: '16px', color: '#1a202c' }}>
                                        {selectedContact.phone || <span style={{ color: '#a0aec0' }}>Not provided</span>}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Subject</label>
                                    <div style={{ fontSize: '16px', color: '#1a202c' }}>{selectedContact.subject}</div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Service Interest</label>
                                    <div style={{ fontSize: '16px', color: '#1a202c' }}>
                                        {selectedContact.requestedService || selectedContact.service || <span style={{ color: '#a0aec0' }}>Not specified</span>}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Message</label>
                                    <div style={{
                                        fontSize: '15px',
                                        color: '#1a202c',
                                        background: '#f7fafc',
                                        padding: '16px',
                                        borderRadius: '0',
                                        lineHeight: '1.6',
                                        marginTop: '8px'
                                    }}>
                                        {selectedContact.message}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Status</label>
                                    <div style={{ marginTop: '8px' }}>
                                        <select
                                            value={selectedContact.status}
                                            onChange={(e) => {
                                                handleUpdateStatus(selectedContact.id, e.target.value);
                                                setSelectedContact({ ...selectedContact, status: e.target.value });
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '0',
                                                fontSize: '14px',
                                                border: '1px solid #e2e8f0',
                                                background: 'white',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {statusOptions.map(status => (
                                                <option key={status} value={status}>{statusColors[status].label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Submitted</label>
                                    <div style={{ fontSize: '14px', color: '#4a5568' }}>
                                        {new Date(selectedContact.createdAt).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    style={{
                                        padding: '12px 24px',
                                        background: '#e2e8f0',
                                        color: '#4a5568',
                                        border: 'none',
                                        borderRadius: '0',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Close
                                </button>
                                <a
                                    href={`mailto:${selectedContact.email}`}
                                    style={{
                                        padding: '12px 24px',
                                        background: '#62B6E8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '0',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        textDecoration: 'none'
                                    }}
                                >
                                    <i className="fa fa-envelope"></i> Reply
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default ContactsManagement;
